'use client';

import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useGlobal } from '@rcg/ui-react';
import getBrand from '../getBrand';

/**
 * Hidden global-login component for session monitoring across all pages.
 * This component:
 * - Renders <global-login> off-screen (visually hidden)
 * - Enables session expiration modals to display globally
 * - Monitors session state via singleton service even when user navigates away
 * - Does NOT show the login form (that's only on /signin page)
 */
const SessionMonitor = ({ logOut }) => {
  const globalLoginRef = useRef(null);
  const brand = getBrand().id === 'cel' ? 'celebrity' : 'royal';
  const [environment, setEnvironment] = useState('production');
  const {
    data: { lang },
  } = useGlobal();

  useEffect(() => {
    // Listen for session events from @rccl/global-login
    // These events are dispatched to document by the package's session monitoring service,
    // which continues running even after <global-login> component unmounts
    const handleSessionExpired = () => {
      logOut();
    };

    const handleSessionNotExtended = () => {
      logOut();
    };

    // Add listeners for global-login session events (dispatched to document)
    document.addEventListener('sessionExpired', handleSessionExpired);
    document.addEventListener('sessionNotExtended', handleSessionNotExtended);

    return () => {
      document.removeEventListener('sessionExpired', handleSessionExpired);
      document.removeEventListener('sessionNotExtended', handleSessionNotExtended);
    };
  }, [logOut]);

  useEffect(() => {
    if (process.env.REACT_APP_UNIFIED_PAYMENT_VERSION === 'test') {
      if (brand === 'celebrity') {
        setEnvironment('stage');
      } else {
        setEnvironment(process.env.REACT_APP_UNIFIED_PAYMENT_VERSION);
      }
    }
  }, []);

  return (
    <div
      style={{
        position: 'fixed',
        top: '-9999px',
        left: '-9999px',
        visibility: 'hidden',
        pointerEvents: 'none',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
      }}>
      <global-login
        brand={brand}
        ref={globalLoginRef}
        env={environment}
        language={lang}
        session-expiration-modal-option={JSON.stringify({
          showWarningModal: true,
          minutesToExpiration: 10,
          showExpiredSessionModal: true,
        })}
      />
    </div>
  );
};
SessionMonitor.propTypes = {
  logOut: PropTypes.func,
};
export default SessionMonitor;
